import { useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * usePermissions() — one-shot fetch of the caller's effective per-module
 * permission map (from GET /api/auth/me/permissions). Re-fetches whenever
 * the current user changes so role edits, ownership transfers and login
 * as-different-user all reflect on the next render.
 *
 * `can(module, level)` returns true if the caller holds ≥ `level` on
 * `module`. `hidden(module)` is sugar for `!can(module, "view")` and is
 * what the sidebar consults to hide entries.
 *
 * Levels ordered: none < view < limited < full
 */
const LEVEL_RANK = { none: 0, view: 1, limited: 2, full: 3 };

export function usePermissions() {
  const { user } = useAuth();
  const [perms, setPerms] = useState(null); // null = loading, {} = loaded
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!user || user === false) { setPerms({}); return; }
    try {
      const { data } = await api.get("/auth/me/permissions");
      setPerms(data?.permissions || {});
    } catch (err) {
      setError(err);
      // Fail-open on non-critical read: leave sidebar visible so users
      // aren't locked out of the app if this endpoint hiccups.
      setPerms({});
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const can = useCallback((module, level = "view") => {
    if (!user) return false;
    // Owner short-circuits — matches backend behaviour.
    if ((user.roles || []).includes("owner")) return true;
    if (!perms) return false;
    const actual = perms[module] || "none";
    return LEVEL_RANK[actual] >= LEVEL_RANK[level];
  }, [perms, user]);

  const hidden = useCallback((module) => !can(module, "view"), [can]);

  return useMemo(() => ({
    perms: perms || {},
    can,
    hidden,
    loading: perms === null,
    error,
    refresh: load,
  }), [perms, can, hidden, error, load]);
}
